import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { platformApi } from '@/services/platformApi';
import { pickProfileBalanceForCurrency } from '@vaiu/utils';

const isEmbeddedFrame = () =>
  typeof window !== 'undefined' && window.parent != null && window.parent !== window;

const UIAPIPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user, loading, isGuest } = useAuth();
  const [processed, setProcessed] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const embedded = isEmbeddedFrame();

  const postToParent = (payload: object) => {
    if (embedded) {
      window.parent.postMessage(payload, '*');
      console.log('UIAPIPage: Sent to parent window:', payload);
    } else {
      console.log('UIAPIPage: No parent window detected, response would be:', payload);
    }
  };

  const resolveUsername = () =>
    user ? (user.username || user.emails?.[0]?.address || user._id || '') : '';

  useEffect(() => {
    if (processed) return;

    const getParam = searchParams.get('get');
    if (!getParam) return;

    if (getParam === 'session') {
      if (loading) return;
      postToParent({
        type: 'vaiu',
        get: 'session',
        authenticated: Boolean(user),
        isGuest: Boolean(user && isGuest),
        username: resolveUsername(),
        userId: user?._id || (user as { userId?: string } | null)?.userId || '',
      });
      setProcessed(true);
      return;
    }

    if (getParam === 'username') {
      if (loading) return;
      postToParent({
        type: 'vaiu',
        get: 'username',
        authenticated: Boolean(user),
        username: resolveUsername(),
      });
      setProcessed(true);
      return;
    }

    if (getParam === 'balance') {
      if (loading) return;
      const currencyRaw = (searchParams.get('currency') ?? '').trim();
      if (!currencyRaw) {
        postToParent({
          type: 'vaiu',
          get: 'balance',
          error: 'missing_currency',
        });
        setProcessed(true);
        return;
      }
      if (!user || isGuest) {
        postToParent({
          type: 'vaiu',
          get: 'balance',
          authenticated: false,
          reason: !user ? 'not_logged_in' : 'guest',
        });
        setProcessed(true);
        return;
      }

      let cancelled = false;
      setBalanceLoading(true);
      (async () => {
        try {
          const balances = await platformApi.getBalances();
          if (cancelled) return;
          const picked = pickProfileBalanceForCurrency(balances, currencyRaw);
          postToParent({
            type: 'vaiu',
            get: 'balance',
            authenticated: true,
            currency: picked.key,
            amount: picked.amount,
            balances: { [picked.key]: picked.amount },
            hasBalanceEntry: picked.hasEntry,
          });
        } catch (e) {
          if (!cancelled) {
            postToParent({
              type: 'vaiu',
              get: 'balance',
              error: 'fetch_failed',
              message: e instanceof Error ? e.message : 'Unknown error',
            });
          }
        } finally {
          if (!cancelled) {
            setBalanceLoading(false);
            setProcessed(true);
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (loading) return;
    console.log('UIAPIPage: Unsupported get parameter:', getParam);
    postToParent({
      type: 'vaiu',
      get: getParam,
      error: 'unsupported_get',
    });
    setProcessed(true);
  }, [searchParams, user, loading, processed, isGuest, embedded]);

  const showLoading = (!processed && loading) || balanceLoading;

  // Embedded UIAPI is a postMessage bridge — no visible chrome (supports 0×0 iframes).
  if (embedded) {
    return null;
  }

  if (showLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>API Interface</h2>
        <p>Loading...</p>
      </div>
    );
  }

  const getParam = searchParams.get('get');
  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>API Interface</h2>
      <p>{processed ? 'Request processed' : 'Processing request...'}</p>
      {getParam === 'session' && (
        <p>
          Session: {user ? (isGuest ? 'guest' : 'signed in') : 'not logged in'} — see console if not embedded.
        </p>
      )}
      {getParam === 'username' && (
        <p>Username: {user ? resolveUsername() || 'Anonymous' : 'Not logged in'}</p>
      )}
      {getParam === 'balance' && (
        <p>
          Balance query
          {searchParams.get('currency') ? ` (${searchParams.get('currency')})` : ''}
          {user && !isGuest ? ' — see parent window message for amount.' : ' — not available (not signed in).'}
        </p>
      )}
    </div>
  );
};

export default UIAPIPage;
